<script lang="ts" setup>
import type { MentionableUser } from '@models/interfaces/mentionableUser';
import { ref, computed, getCurrentInstance, watch } from 'vue';
import { QuillEditor } from '@vueup/vue-quill';
import Quill from 'quill';
import type { QuillEditor as QuillEditorType } from '@vueup/vue-quill';
import { type UUIDv4 } from '@global-types/uuid.ts';
import Button from '@components/Button.vue';
import HorizontalList from '@components-molecules/HorizontalList.vue';
import { eventBus } from '@tools/event-bus';
import { trans, transchoice } from '@elements/translation';
import MentionBox from './ContentVueEditor/MentionBox.vue';
import { UserMentionModule, QUILL_USER_MENTION_EVENT_SEARCH, QUILL_USER_MENTION_EVENT_EXIT } from './ContentVueEditor/userMentionModule.ts';

declare global {
  interface Window {
    USERFILE_PREVIEWABLE_MIMETYPES: string[];
  }
}

declare type QuillModule = {
  name: string;
  module: unknown;
  options?: object;
}

declare type QuillToolbarModule = {
  handlers: Record<string, (value?: unknown) => void>;
  addHandler?: (name: string, handler: (value?: unknown) => void) => void;
};

interface Attachment {
  uuid: UUIDv4;
  name: string;
  mimetype: string;
}

const props = withDefaults(
  defineProps<{
    modelValue: string;
    disableLink?: boolean;
    showSubmit?: boolean;
    minCounter?: number;
    maxCounter?: number;
    placeholder?: string;
    loading?: boolean;
    attachments?: Attachment[];
    users?: MentionableUser[];
    enableMentions?: boolean;
  }>(),
  {
    disableLink: false,
    showSubmit: false,
    loading: false,
    attachments: () => [],
    users: () => [],
    enableMentions: false,
  },
);

const emit = defineEmits<{(e: 'update:modelValue', payload: string): void;
  (e: 'removeAttachment', index: number): void;
  (e: 'submit'): void;
}>();

const instance = getCurrentInstance();
const uid = String(instance?.uid ?? Math.floor(Math.random() * 10000));
const toolbarId = `quill-toolbar-${uid}`;

const html = computed<string>({
  get: () => props.modelValue,
  set: (value: string) => emit('update:modelValue', value),
});

const editorContainer = ref<HTMLDivElement>();
const quillRef = ref<InstanceType<typeof QuillEditorType>>();
const quillInstance = computed<Quill>(() => quillRef.value!.getQuill());

const text = computed<string>(() => html.value.replace(/<[^>]*>|\r?\n/g, '').trim());

const hasCounter = computed<boolean>(() => props.maxCounter !== undefined || props.minCounter !== undefined);
const counter = computed<number>(() => text.value.length);
const canSubmit = computed<boolean>(() => (text.value.length > 0 || props.attachments.length > 0) && props.loading === false);

const modules = ref<QuillModule[]>([]);
const mention = ref(null);
if (props.enableMentions) {
  modules.value.push({
    name: 'user-mention',
    module: UserMentionModule,
  });

  eventBus.addEventListener(QUILL_USER_MENTION_EVENT_SEARCH, (event: CustomEvent) => {
    mention.value = event.detail.search;
  });

  eventBus.addEventListener(QUILL_USER_MENTION_EVENT_EXIT, () => {
    mention.value = null;
  });
}

const editorIsFocused = ref<boolean>(false);

watch(editorIsFocused, (value) => {
  if (!props.enableMentions) {
    return;
  }
  // The placeholder is implemented with a CSS rule in Quill, so :placeholder attribute is not dynamic, use native hack...
  const editorElement = editorContainer.value!.querySelector('.ql-editor');
  if (editorElement) {
    editorElement.setAttribute('data-placeholder', value ? trans('content_editor.mention.placeholder') : (props.placeholder ?? ''));
  }
});

const mentionUser = (user: MentionableUser) => {
  const mentionModule = quillInstance.value.getModule('user-mention') as UserMentionModule;
  mentionModule.selectUser(user);
};

const watchModelValue = (): void => {
  watch(() => props.modelValue, (value) => {
    if (value.trim().length === 0) {
      quillInstance.value?.setText('');
    }
  });
};

const setupLinkTool = (): void => {
  if (props.disableLink) {
    return;
  }
  const toolbar = quillInstance.value.getModule('toolbar') as QuillToolbarModule;
  toolbar.handlers.link = (): void => {
    const range = quillInstance.value.getSelection();
    if (!range) {
      return;
    }
    // eslint-disable-next-line no-alert
    const url = prompt(trans('content_editor.link.message'));
    if (url && /^https?:\/\//i.test(url)) {
      quillInstance.value.format('link', url);
    } else if (url) {
      // eslint-disable-next-line no-alert
      alert(trans('content_editor.link.error'));
    }
  };
};

const setupKeyListeners = (): void => {
  quillInstance.value.root.addEventListener('keydown', (e: KeyboardEvent) => {
    // Hack for implementation on search
    if (e.key === 'Enter') {
      setTimeout(() => quillInstance.value.focus(), 0);
    }
  });
};

const setupQuillContainer = (): void => {
  if (!props.showSubmit) {
    return;
  }

  const editorElement = editorContainer.value?.querySelector('.ql-editor');
  if (editorElement) {
    editorElement.classList.add('ql-editor--with-submit');
  }
};

const quillReady = (): void => {
  watchModelValue();
  setupLinkTool();
  setupKeyListeners();
  setupQuillContainer();
};

const isFileAnImage = (file: Attachment): boolean => Object.values(window.USERFILE_PREVIEWABLE_MIMETYPES).includes(file.mimetype);

const removeAttachment = (index: number) => {
  emit('removeAttachment', index);
};

const submit = () => {
  emit('submit');
};
</script>

<template>
  <div class="content-vue-editor">
    <div
      ref="editorContainer"
      class="content-vue-editor__quill editor-quill"
      :class="{ 'editor-quill--attachments': attachments.length > 0 }"
    >
      <QuillEditor
        ref="quillRef"
        v-model:content="html"
        :placeholder="placeholder"
        content-type="html"
        theme="snow"
        :toolbar="`#${toolbarId}`"
        :modules="modules"
        @ready="quillReady"
        @focus="editorIsFocused = true"
        @blur="editorIsFocused = false"
      >
        <template #toolbar>
          <div :id="toolbarId" class="editor__toolbar">
            <button class="ql-bold" />
            <button class="ql-italic" />
            <button class="ql-underline" />
            <button class="ql-list" value="bullet" />
            <button v-if="!disableLink" class="ql-link" />
          </div>
        </template>
      </QuillEditor>

      <MentionBox
        v-if="mention !== null"
        :mention="mention"
        :users="users"
        @select-user="mentionUser"
        @close="mention = null"
      />

      <div class="editor-quill__actions quill-actions">
        <div
          v-if="attachments.length > 0"
          class="quill-actions__attachments actions-attachments"
        >
          <HorizontalList :length="attachments.length">
            <template #items>
              <div
                v-for="(attachment, index) in attachments"
                :key="attachment.uuid"
                :data-offset="index"
                class="actions-attachments__item attachements-item"
              >
                <span
                  class="attachements-item__icon item-icon"
                  :class="{ 'item-icon--img': isFileAnImage(attachment) }"
                />
                <span class="attachements-item__text item-text">{{ attachment.name }}</span>
                <span
                  class="attachements-item__remove item-remove"
                  @click="removeAttachment(Number(index))"
                />
              </div>
            </template>
          </HorizontalList>
        </div>

        <Button
          v-if="showSubmit"
          variant="primary"
          class="quill-actions__submit actions-submit"
          :disabled="!canSubmit"
          @click="submit"
        >
          <span class="actions-submit__content submit-content" />
        </Button>
      </div>
    </div>

    <div v-if="hasCounter" class="content-vue-editor__counter editor-counter">
      {{ transchoice('global.char_counter', { count: counter }) }}
      <template v-if="minCounter != undefined">
        • {{ trans('global.min_counter', { count: minCounter }) }}
      </template>
      <template v-if="maxCounter != undefined">
        • {{ trans('global.max_counter', { count: maxCounter }) }}
      </template>
    </div>
  </div>
</template>
